import React, { useMemo, useState } from "react";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getMonthsLong, getWeekdaysShort } from "../constants";

export function toISO(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export interface MonthCalendarProps {
  selectedDates: Array<string>;
  minDate?: Date;
  onToggle: (isoDate: string) => void;
}

export function MonthCalendar({
  selectedDates,
  minDate,
  onToggle,
}: MonthCalendarProps) {
  const { t, i18n } = useTranslation("common");
  const lng = i18n.language || "en";
  const months = useMemo(() => getMonthsLong(lng), [lng]);
  const weekdaysShort = useMemo(() => getWeekdaysShort(lng), [lng]);

  const min = startOfDay(minDate ?? new Date());
  const [view, setView] = useState({
    y: min.getFullYear(),
    m: min.getMonth(),
  });

  const atMinMonth = view.y === min.getFullYear() && view.m === min.getMonth();
  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const selected = new Set(selectedDates);

  const prev = () =>
    setView((v) =>
      v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 },
    );
  const next = () =>
    setView((v) =>
      v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 },
    );

  const cells: Array<React.ReactNode> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(<div key={`b${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const id = toISO(view.y, view.m, d);
    const date = new Date(view.y, view.m, d);
    const isPast = date < min;
    const isToday = date.getTime() === min.getTime();
    const isSel = selected.has(id);
    cells.push(
      <button
        key={id}
        type="button"
        className={`av-day${isSel ? " is-sel" : ""}${isPast ? " is-off" : ""}${isToday ? " is-today" : ""}`}
        disabled={isPast}
        aria-pressed={isSel}
        aria-label={`${months[view.m]} ${d}, ${view.y}`}
        onClick={() => onToggle(id)}
      >
        {d}
      </button>,
    );
  }

  return (
    <div>
      <div className="av-cal-head">
        <button
          type="button"
          className="av-nav"
          onClick={prev}
          disabled={atMinMonth}
          aria-label={t("availabilityPicker.previousMonth")}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="av-cal-title">
          {months[view.m]} {view.y}
        </span>
        <button
          type="button"
          className="av-nav"
          onClick={next}
          aria-label={t("availabilityPicker.nextMonth")}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="av-grid" style={{ marginBottom: 2 }}>
        {weekdaysShort.map((w) => (
          <div key={w} className="av-dow">
            {w}
          </div>
        ))}
      </div>
      <div className="av-grid">{cells}</div>
    </div>
  );
}
